import useSWR, {mutate} from 'swr'
import {useState} from 'react'
import map from 'lodash/map'
import hash from 'stable-hash'
import {
  fetchSchool,
  fetchSchools,
  fetchSchoolsByDistrict,
  updateSchool as updateSchoolApi,
  deleteIdentityProviderAssociations,
  fetchSchoolDeletionInfo,
  deleteSchool,
  postSchoolMerge
} from '@frontrow/console/src/helpers/api-schools'
import {
  parse as parseSchool,
  parseDetailedSchool,
  parseDetailedSchools,
  type BasicSchoolWithRenaissanceClientT,
  type DetailedSchoolT,
  type SchoolAttrs,
  type SchoolDeletionInfoT
} from '@frontrow/console/src/models/school'
import {
  responseApiErrors,
  firstResponseApiError
} from '@frontrow/console/src/entities/models/api-error'
import {type FetchDataParamsT} from '@frontrow/console/src/components/common/data-table'
import {parallelLimit} from '@frontrow/console/src/entities/helpers/control-flow'
import {
  type BulkUpdateSchoolConfigurationDataT,
  type SchoolBatchUpdateResponseT
} from '@frontrow/console/src/models/school-batch-update'

// SWR cache key constants
const SCHOOL_CACHE_KEY = 'school'
const SCHOOLS_CACHE_KEY = 'schools'
const SCHOOLS_BY_DISTRICT_CACHE_KEY = 'schools-by-district'
const SCHOOL_DELETION_INFO_CACHE_KEY = 'school-deletion-info'

type UseSchoolResult = {
  school: DetailedSchoolT | undefined
  isLoading: boolean
  error: string | undefined
  mutate: () => void
}

export function useSchool(schoolId: number): UseSchoolResult {
  const {data, error, isLoading, mutate} = useSWR([SCHOOL_CACHE_KEY, schoolId], async ([_, id]) => {
    const response = await fetchSchool(id)
    return parseDetailedSchool(response)
  })

  return {
    school: data,
    isLoading,
    error: error?.message,
    mutate
  }
}

type UseSchoolsResult = {
  schools: Array<DetailedSchoolT> | undefined
  isLoading: boolean
  error: string | undefined
}

export function useSchools(fetchDataParams: FetchDataParamsT | null): UseSchoolsResult {
  const cacheKey = fetchDataParams ? [SCHOOLS_CACHE_KEY, hash(fetchDataParams)] : null

  const {data, error, isLoading} = useSWR(cacheKey, async () => {
    const response = await fetchSchools(fetchDataParams)
    return parseDetailedSchools(response)
  })

  return {
    schools: data,
    isLoading,
    error: error?.message
  }
}

type UseSchoolsByDistrictResult = {
  schools: Array<SchoolAttrs> | undefined
  isLoading: boolean
  error: string | undefined
  mutate: () => void
}

export function useSchoolsByDistrict(
  districtId: number | null | undefined
): UseSchoolsByDistrictResult {
  const {data, error, isLoading, mutate} = useSWR(
    districtId ? [SCHOOLS_BY_DISTRICT_CACHE_KEY, districtId] : null,
    async ([_, id]) => {
      const response = await fetchSchoolsByDistrict(id)
      return response.map(parseSchool)
    }
  )

  return {
    schools: data,
    isLoading,
    error: error?.message,
    mutate
  }
}

type UpdateSchoolResult = {
  updateSchool: (
    schoolId: number,
    updatedSchool: BasicSchoolWithRenaissanceClientT,
    shouldDeleteCleverAndClasslinkAssociations?: boolean
  ) => Promise<DetailedSchoolT>
  isUpdating: boolean
  error: string | undefined
}

export function useUpdateSchool(): UpdateSchoolResult {
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const updateSchool = async (
    schoolId: number,
    updatedSchool: BasicSchoolWithRenaissanceClientT,
    shouldDeleteCleverAndClasslinkAssociations: boolean = false
  ): Promise<DetailedSchoolT> => {
    setIsUpdating(true)
    setError(undefined)

    try {
      if (shouldDeleteCleverAndClasslinkAssociations) {
        await deleteIdentityProviderAssociations(schoolId)
      }

      const response = await updateSchoolApi(schoolId, updatedSchool)
      const updatedSchoolData = parseDetailedSchool(response)

      // Invalidate the cache for this school
      mutate([SCHOOL_CACHE_KEY, schoolId], updatedSchoolData, false)

      // Invalidate all schools lists so they refetch with updated school data
      mutate(key => Array.isArray(key) && key[0] === SCHOOLS_CACHE_KEY, undefined, {
        revalidate: true
      })

      setIsUpdating(false)
      return updatedSchoolData
    } catch (err: unknown) {
      const apiErrors = responseApiErrors(err)
      const message = apiErrors.map(e => e.message).join('\n')
      setError(message)
      setIsUpdating(false)
      throw new Error(message)
    }
  }

  return {
    updateSchool,
    isUpdating,
    error
  }
}

type UseSchoolDeletionInfoResult = {
  schoolDeletionInfo: SchoolDeletionInfoT | undefined
  isLoading: boolean
  error: string | undefined
}

export function useSchoolDeletionInfo(
  schoolId: number,
  enabled: boolean = false
): UseSchoolDeletionInfoResult {
  const {data, error, isLoading} = useSWR(
    enabled ? [SCHOOL_DELETION_INFO_CACHE_KEY, schoolId] : null,
    async ([_, id]) => {
      return fetchSchoolDeletionInfo(id)
    }
  )

  return {
    schoolDeletionInfo: data,
    isLoading,
    error: error?.message
  }
}

type DeleteSchoolResult = {
  deleteSchool: (schoolId: number) => Promise<SchoolAttrs>
  isDeleting: boolean
  error: string | undefined
}

export function useDeleteSchool(): DeleteSchoolResult {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const removeSchool = async (schoolId: number): Promise<SchoolAttrs> => {
    setIsDeleting(true)
    setError(undefined)

    try {
      const response = await deleteSchool(schoolId)
      const deletedSchool = parseSchool(response)

      // Invalidate the cache for this school
      mutate([SCHOOL_CACHE_KEY, schoolId], undefined, false)

      // Invalidate all schools lists so they refetch without the deleted school
      mutate(key => Array.isArray(key) && key[0] === SCHOOLS_CACHE_KEY, undefined, {
        revalidate: true
      })

      // Invalidate all schools-by-district caches
      mutate(key => Array.isArray(key) && key[0] === SCHOOLS_BY_DISTRICT_CACHE_KEY, undefined, {
        revalidate: true
      })

      setIsDeleting(false)
      return deletedSchool
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err, 'Could not delete school')
      setError(apiError.message)
      setIsDeleting(false)
      throw new Error(apiError.message)
    }
  }

  return {
    deleteSchool: removeSchool,
    isDeleting,
    error
  }
}

type MergeSchoolResult = {
  mergeSchool: (sourceSchoolId: number, targetSchoolId: number) => Promise<void>
  isMerging: boolean
  error: string | undefined
}

export function useMergeSchool(): MergeSchoolResult {
  const [isMerging, setIsMerging] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const merge = async (sourceSchoolId: number, targetSchoolId: number): Promise<void> => {
    setIsMerging(true)
    setError(undefined)

    try {
      await postSchoolMerge(sourceSchoolId, targetSchoolId)
      setIsMerging(false)
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err, 'Could not merge school')
      setError(apiError.message)
      setIsMerging(false)
      throw new Error(apiError.message)
    }
  }

  return {
    mergeSchool: merge,
    isMerging,
    error
  }
}

type BatchUpdateSchoolConfigurationResult = {
  batchUpdate: (
    schools: Array<BulkUpdateSchoolConfigurationDataT>
  ) => Promise<SchoolBatchUpdateResponseT>
  isUpdating: boolean
  error: string | undefined
}

export function useBatchUpdateSchoolConfiguration(): BatchUpdateSchoolConfigurationResult {
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const batchUpdate = async (
    updatedSchoolConfigurations: Array<BulkUpdateSchoolConfigurationDataT>
  ): Promise<SchoolBatchUpdateResponseT> => {
    setIsUpdating(true)
    setError(undefined)

    try {
      // Create tasks for parallelLimit
      const tasks = map(updatedSchoolConfigurations, school => {
        return function (): Promise<DetailedSchoolT> {
          const {shouldDeleteCleverAndClasslinkAssociations, id, ...attrsToUpdate} = school

          const firstDeleteAssociations = shouldDeleteCleverAndClasslinkAssociations
            ? deleteIdentityProviderAssociations(id)
            : Promise.resolve(undefined)

          return firstDeleteAssociations
            .then(() => updateSchoolApi(id, attrsToUpdate))
            .then(parseDetailedSchool)
        }
      })

      // Execute with parallel limit
      const {errors, results} = await parallelLimit(tasks, 20)

      // Parse errors
      const parsedErrors = map(errors, error => responseApiErrors(error))

      // Invalidate all schools caches
      mutate(
        key =>
          Array.isArray(key) &&
          (key[0] === SCHOOLS_CACHE_KEY || key[0] === SCHOOLS_BY_DISTRICT_CACHE_KEY),
        undefined,
        {revalidate: true}
      )

      setIsUpdating(false)
      return {errors: parsedErrors, results}
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err, 'Batch update failed')
      setError(apiError.message)
      setIsUpdating(false)
      throw new Error(apiError.message)
    }
  }

  return {batchUpdate, isUpdating, error}
}
